/**
 * `npcDossier` — the pure tier→sections decision model behind the player's NPC dossier. This repo
 * has no jsdom/headless React renderer, so — mirroring `disclosure.ts`/`npcFeatures.ts`/
 * `combatInspector.ts` — the render decision is a pure classifier `PlayerNpcInspectorBody` calls
 * rather than re-deriving.
 *
 * The panel set: IDENTITY · THREAT (weapons) · TRAITS · SYSTEMS · REACTIONS · TECH · STATUS ·
 * VITALS. **Every section is present at every disclosure tier** — locked, not missing — and this
 * module decides only WHICH state (`"revealed" | "locked"`) each section carries at the combatant's
 * current tier. It deliberately does **not** decide section CONTENT (which weapons render, which
 * features group where) — that stays in `npcFeatures.ts` / `weaponFormat.ts`, read directly by the
 * component once a section is `"revealed"`.
 *
 * **No action/economy section exists in this model, at any tier** — the grid, the turn-economy
 * readout, ACTIVATE/END TURN and the rest of the GM's control chrome carry no player-facing intel
 * and are structurally absent from {@link NPC_DOSSIER_SECTION_ORDER}, not merely unrendered.
 *
 * Reads the projected combatant's OWN shape — {@link isMaskedCombatant} plus its `disclosure`
 * tier when masked — never a second masking mechanism and never a filter of `state.combatants`.
 * The three-tier map:
 *   - **`"class"`** (incl. never-set) — only the {@link ClassMaskedCombatant}'s
 *     `MaskedKeepFields` + `ClassTierBase` knowable set exists at all; THREAT/TRAITS/SYSTEMS/
 *     REACTIONS/TECH/VITALS are `"locked"`. A feature the unit revealed by using it
 *     (`decisions/0207`) still rides `ClassTierBase` and the renderer shows its row beside the
 *     lock; the section itself stays `"locked"` at this tier, and its count (`redactedCounts`)
 *     counts only the unrevealed.
 *   - **`"loadout"`** — `base.weapons`/`base.npcFeatures`/`base.equippedSystems` are now
 *     present, so THREAT/TRAITS/SYSTEMS/REACTIONS/TECH unlock; VITALS (the whole `current`
 *     block) is still absent, so it stays `"locked"`.
 *   - **`"full"`** — `current` is present (a real `Combatant` or an {@link HpHiddenCombatant}),
 *     so VITALS unlocks too. Whether HP itself still reads `?? / max` under the `showEnemyHp`
 *     policy is a rendering concern layered on top of an already-`"revealed"` VITALS section,
 *     not a fourth tier here.
 *
 * IDENTITY and STATUS are `"revealed"` at every tier: identity off `MaskedKeepFields`
 * (`name`/`sourceRef`/`tier`/`templateIds`) is knowable from `"class"` on, and `statuses` are
 * visible at every disclosure level.
 *
 * `npcClass` is accepted but unused past a presence check, so a renderer can thread its
 * `compendium.getById("npc_class", c.sourceRef)` lookup through this seam without a signature
 * reshape when the IDENTITY section grows `info.terse`/`info.flavor` content.
 */
import type { NpcClass } from "@lancer-vtt/compendium";
import type { ProjectedCombatant } from "@lancer-vtt/domain";
import { isMaskedCombatant } from "./disclosure";

/** One of the player NPC dossier's eight sections — see this module's doc for the full set. */
export type NpcDossierSectionKind =
  | "identity"
  | "threat"
  | "traits"
  | "systems"
  | "reactions"
  | "tech"
  | "status"
  | "vitals";

/** Whether a dossier section's content is knowable at the combatant's current disclosure tier. */
export type NpcDossierSectionState = "revealed" | "locked";

/**
 * One row of the dossier's section list: which section, and whether it's `"revealed"` or
 * `"locked"` at the combatant's current tier. Every section is present in every call's
 * output — never omitted, per the goal's "locked, not missing" design.
 */
export type NpcDossierSection = {
  kind: NpcDossierSectionKind;
  state: NpcDossierSectionState;
  /**
   * The redacted count for a still-`"locked"` section (rendered `WEAPONS ▪ 3 ▪ LOCKED`), sourced
   * from `ProjectedCombatant`'s own `redactedCounts` field (`@lancer-vtt/domain`'s
   * `ClassMaskedCombatant`) — never re-derived here. **Only ever set for a `"locked"` THREAT/
   * TRAITS/SYSTEMS/REACTIONS/TECH section at the `"class"` tier** — the one tier that carries
   * `redactedCounts` at all (see that type's doc for why `"loadout"` doesn't). `threat`'s count
   * is `redactedCounts.weapons`; `traits`/`systems`/`reactions`/`tech` all carry the SAME
   * `redactedCounts.features` value — a single aggregate across all four `npcFeatures` kinds, not
   * a per-section breakdown (the shape is exactly `{weapons, features}`). A renderer must
   * treat that number as "N feature entries exist somewhere across these four sections," not "this
   * section has N." `identity`/`status` (always revealed) and `vitals` (locked but not a countable
   * quantity) never carry a count, at any tier.
   */
  count?: number;
};

/**
 * The dossier's section order — fixed, and the complete set at every tier. This list's
 * exhaustiveness is what enforces "no action/economy section exists in the model at any tier".
 */
export const NPC_DOSSIER_SECTION_ORDER: readonly NpcDossierSectionKind[] = [
  "identity",
  "threat",
  "traits",
  "systems",
  "reactions",
  "tech",
  "status",
  "vitals",
] as const;

/** Sections revealed at every tier, including `"class"` — identity and observable statuses. */
const ALWAYS_REVEALED = new Set<NpcDossierSectionKind>(["identity", "status"]);

/** Sections that unlock at `"loadout"` — weapons + the four `npcFeatures` groupings. */
const LOADOUT_REVEALED = new Set<NpcDossierSectionKind>(["threat", "traits", "systems", "reactions", "tech"]);

/**
 * The combatant's disclosure tier, read off its OWN projected shape — never a second masking
 * path. A masked combatant ({@link isMaskedCombatant}) carries its tier directly on
 * `disclosure` (`"class" | "loadout"`); an unmasked one (`current` present — a real
 * `Combatant`, ally, GM view, or an HP-hidden `"full"`-disclosed hostile) is always `"full"`
 * from a render standpoint, since VITALS (`current`) is present either way.
 */
function tierOf(c: ProjectedCombatant): "class" | "loadout" | "full" {
  return isMaskedCombatant(c) ? c.disclosure : "full";
}

/** One section's `"revealed" | "locked"` state at a given tier — the section × tier matrix. */
function sectionState(kind: NpcDossierSectionKind, tier: "class" | "loadout" | "full"): NpcDossierSectionState {
  if (ALWAYS_REVEALED.has(kind)) return "revealed";
  if (kind === "vitals") return tier === "full" ? "revealed" : "locked";
  // threat / traits / systems / reactions / tech
  return LOADOUT_REVEALED.has(kind) && tier !== "class" ? "revealed" : "locked";
}

/**
 * The redacted count for one `"locked"` section, or `undefined` when the
 * combatant's projected shape carries no `redactedCounts` (every tier but `"class"`) or the
 * section isn't one of the two count-eligible buckets (`threat`'s own weapons, or the
 * `traits`/`systems`/`reactions`/`tech` shared `features` aggregate — see `NpcDossierSection.
 * count`'s doc). Never called for a `"revealed"` section by {@link buildNpcDossierSections} — a
 * revealed section renders its real content, not a placeholder count.
 */
function redactedCountFor(kind: NpcDossierSectionKind, combatant: ProjectedCombatant): number | undefined {
  if (!isMaskedCombatant(combatant) || combatant.disclosure !== "class") return undefined;
  if (kind === "threat") return combatant.redactedCounts.weapons;
  if (kind === "traits" || kind === "systems" || kind === "reactions" || kind === "tech") {
    return combatant.redactedCounts.features;
  }
  return undefined;
}

/**
 * Build the player NPC dossier's ordered section list for one combatant — the model
 * `PlayerNpcInspectorBody` renders from. `npcClass` is the caller's already-resolved
 * `compendium.getById("npc_class", c.sourceRef)` lookup, or `undefined` when no NPC pack is
 * loaded; it is accepted but not yet read (see this module's doc).
 */
export function buildNpcDossierSections(combatant: ProjectedCombatant, npcClass?: NpcClass): NpcDossierSection[] {
  void npcClass; // reserved for the IDENTITY section's class-info content — this model decides lock state only.
  const tier = tierOf(combatant);
  return NPC_DOSSIER_SECTION_ORDER.map((kind) => {
    const state = sectionState(kind, tier);
    if (state !== "locked") return { kind, state };
    const count = redactedCountFor(kind, combatant);
    return count === undefined ? { kind, state } : { kind, state, count };
  });
}

/**
 * How many of the hostile's weapons and features the sections still lock: the masked sheet's one
 * locked row, `N FEATURES NOT SEEN`. An NPC's weapons are features too, so this is THREAT's weapon
 * count plus the feature aggregate, counted once (TRAITS, SYSTEMS, REACTIONS and TECH all carry the
 * same `redactedCounts.features`, see {@link NpcDossierSection.count}). 0 when nothing is locked, or
 * at a tier that carries no counts.
 */
export function lockedFeatureCount(sections: readonly NpcDossierSection[]): number {
  const countOf = (kind: NpcDossierSectionKind) => sections.find((s) => s.kind === kind && s.state === "locked")?.count ?? 0;
  return countOf("threat") + countOf("traits");
}
